import { useQuery } from '@tanstack/react-query';
import { apiHelpers } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { formatNumber } from '@/lib/utils';
import { can } from '@/types';
import type { Capability } from '@/types';
import { Link } from 'react-router-dom';
import {
  FolderOpen,
  Users,
  Mail,
  MessageSquare,
  ArrowRight,
  Puzzle,
  UsersRound,
  BarChart3,
  Brain,
  Settings,
  PlusCircle,
  UserPlus,
  Sparkles,
  Globe,
} from 'lucide-react';

export default function Dashboard() {
  const { user } = useAuthStore();

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => apiHelpers.getProjects({ limit: 5 }),
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiHelpers.getUsers({ limit: 5 }),
    enabled: user?.role === 'SUPER_ADMIN', // Users list is SUPER_ADMIN only
  });

  const { data: globalStatsData } = useQuery({
    queryKey: ['global-stats'],
    queryFn: () => apiHelpers.getGlobalStats(),
  });

  const projects = projectsData?.data?.data || [];
  const globalStats = globalStatsData?.data?.data;

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  // Note: isSuperAdmin is used for platform-wide stats, not capability-gated features

  const hasNoTeam = !isSuperAdmin && !user?.teamId && projects.length === 0;
  // The customer's website (one project per team). Drives the primary CTA.
  const myProject: any = !isSuperAdmin ? projects[0] : null;
  const websiteLive = Boolean(myProject?.workerScriptName);

  const stats = [
    // Platform-wide metrics — only meaningful for super admins
    ...(isSuperAdmin
      ? [
          {
            name: 'Total Projects',
            value: projectsData?.data?.pagination?.total ?? projects.length,
            icon: FolderOpen,
            color: 'bg-blue-500',
            href: '/projects',
          },
          {
            name: 'Active Users',
            value: usersData?.data?.pagination?.total ?? 0,
            icon: Users,
            color: 'bg-green-500',
            href: '/users',
          },
        ]
      : []),
    {
      name: 'Newsletter Subscribers',
      value: globalStats?.newsletterSubscribers ?? '-',
      icon: Mail,
      color: 'bg-purple-500',
      href: myProject ? `/projects/${myProject.id}/newsletter` : '/projects',
    },
    {
      name: 'Contact Submissions',
      value: globalStats?.contactSubmissions ?? '-',
      icon: MessageSquare,
      color: 'bg-orange-500',
      href: myProject ? `/projects/${myProject.id}/contacts` : '/projects',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-2xl font-bold">Welcome back, {user?.firstName}!</h1>
        <p className="text-muted-foreground">Here's what's happening with your projects today.</p>
      </div>

      {/* Primary CTA: the customer's website */}
      {myProject && (
        <div className="from-primary/10 to-primary/5 rounded-xl border bg-gradient-to-r p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-primary flex items-center gap-2 text-sm font-medium">
                <Sparkles className="h-4 w-4" /> Your website
              </p>
              <h2 className="mt-1 text-lg font-semibold">
                {websiteLive ? `${myProject.name} is live` : 'Build your website with AI'}
              </h2>
              <p className="text-muted-foreground mt-1 text-sm">
                {websiteLive
                  ? 'Ask for changes in plain words — they are applied, checked and published for you.'
                  : 'Answer a few questions, describe what you want, and watch it get built — usually 10–25 minutes.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {websiteLive && myProject.sitesUrl && (
                <a
                  href={
                    myProject.domains?.[0] ? `https://${myProject.domains[0]}` : myProject.sitesUrl
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:bg-muted inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium"
                >
                  <Globe className="h-4 w-4" /> Open site
                </a>
              )}
              <Link
                to={websiteLive ? `/projects/${myProject.id}/website` : '/welcome'}
                className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium"
              >
                <Sparkles className="h-4 w-4" /> {websiteLive ? 'Refine with AI' : 'Start building'}
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* No-team empty state */}
      {hasNoTeam && (
        <div className="bg-card rounded-xl border p-8">
          <div className="mx-auto max-w-md text-center">
            <div className="bg-muted mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
              <FolderOpen className="text-muted-foreground h-8 w-8" />
            </div>
            <h2 className="mb-2 text-lg font-semibold">You're not part of a team yet</h2>
            <p className="text-muted-foreground mb-6 text-sm">
              Get started by creating your own project or join an existing team.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Link
                to="/welcome"
                className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors"
              >
                <PlusCircle className="h-4 w-4" />
                Build your website
              </Link>
              <div className="inline-flex items-center justify-center gap-2 rounded-lg border-2 border-dashed px-5 py-2.5 text-sm font-medium">
                <UserPlus className="text-muted-foreground h-4 w-4" />
                <span className="text-muted-foreground">Ask a team owner to invite you</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="bg-card rounded-xl border p-6 transition-shadow hover:shadow-md"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">{stat.name}</p>
                <p className="mt-1 text-2xl font-bold">
                  {typeof stat.value === 'number' ? formatNumber(stat.value) : stat.value}
                </p>
              </div>
              <div className={`rounded-lg p-3 ${stat.color}`}>
                <stat.icon className="h-5 w-5 text-white" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Projects */}
        <div className="bg-card rounded-xl border">
          <div className="flex items-center justify-between border-b p-6">
            <h2 className="font-semibold">Recent Projects</h2>
            <Link
              to="/projects"
              className="text-primary flex items-center gap-1 text-sm hover:underline"
            >
              View all
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="divide-y">
            {projects.length === 0 ? (
              <div className="text-muted-foreground p-6 text-center">
                <FolderOpen className="mx-auto mb-2 h-8 w-8 opacity-50" />
                <p>No projects yet</p>
                <Link
                  to="/welcome"
                  className="text-primary mt-1 inline-block text-sm hover:underline"
                >
                  Build your website
                </Link>
              </div>
            ) : (
              projects.map((project: any) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="hover:bg-muted/50 flex items-center gap-4 p-4 transition-colors"
                >
                  <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-lg">
                    <FolderOpen className="text-primary h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{project.name}</p>
                    <p className="text-muted-foreground truncate text-sm">
                      {project.domain || 'No domain configured'}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-2 py-1 text-xs ${
                      project.status === 'ACTIVE'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                    }`}
                  >
                    {project.status}
                  </span>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions — filtered by capabilities */}
        {(() => {
          const firstProjectId = projects[0]?.id;
          const quickActions: {
            label: string;
            to: string;
            icon: any;
            requires?: Capability;
            superOnly?: boolean;
          }[] = [
            {
              label: 'View Project',
              to: firstProjectId ? `/projects/${firstProjectId}` : '/projects',
              icon: FolderOpen,
              requires: 'view_project',
            },
            {
              label: 'Team Settings',
              to: '/settings/team',
              icon: UsersRound,
              requires: 'invite_members',
            },
            { label: 'Manage Plugins', to: '/plugins', icon: Puzzle, requires: 'manage_plugins' },
            {
              label: 'AI Training',
              to: '/ai-training',
              icon: Brain,
              requires: 'manage_ai_training',
            },
            {
              label: 'Analytics',
              to: firstProjectId ? `/projects/${firstProjectId}/analytics` : '/projects',
              icon: BarChart3,
              requires: 'view_project',
            },
            { label: 'Settings', to: '/settings', icon: Settings },
            { label: 'Manage Users', to: '/users', icon: Users, superOnly: true },
          ];

          const visible = quickActions.filter((a) => {
            if (a.superOnly && user?.role !== 'SUPER_ADMIN') return false;
            if (a.requires && !can(user, a.requires)) return false;
            return true;
          });

          return visible.length > 0 ? (
            <div className="bg-card rounded-xl border">
              <div className="border-b p-6">
                <h2 className="font-semibold">Quick Actions</h2>
              </div>
              <div className="grid grid-cols-2 gap-3 p-4">
                {visible.map((action) => (
                  <Link
                    key={action.label}
                    to={action.to}
                    className="hover:border-primary hover:bg-primary/5 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 transition-colors"
                  >
                    <action.icon className="text-muted-foreground mb-2 h-6 w-6" />
                    <span className="text-sm font-medium">{action.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          ) : null;
        })()}
      </div>

      {/* System Status */}
      {isSuperAdmin && (
        <div className="bg-card rounded-xl border p-6">
          <h2 className="mb-4 font-semibold">System Status</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-center gap-3">
              <div className="h-3 w-3 rounded-full bg-green-500" />
              <div>
                <p className="font-medium">API Server</p>
                <p className="text-muted-foreground text-sm">Operational</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-3 w-3 rounded-full bg-green-500" />
              <div>
                <p className="font-medium">Database</p>
                <p className="text-muted-foreground text-sm">Connected</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-3 w-3 rounded-full bg-green-500" />
              <div>
                <p className="font-medium">Cache</p>
                <p className="text-muted-foreground text-sm">Active</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
